import { useCallback, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuth } from '@/auth/useAuth'
import { useMyMissions } from '@/api/hooks'
import { useMercureSubscription, type MercureEventData } from '@/api/mercure'
import { MissionCard } from '@/components/MissionCard'
import { InProgressPanel } from '@/components/InProgressPanel'
import { FirstPatrolCard } from '@/components/FirstPatrolCard'
import { ReputationCard } from '@/components/ReputationCard'
import { StreakCard } from '@/components/StreakCard'
import { ObjectivesPanel } from '@/components/ObjectivesPanel'
import { SeaScene } from '@/components/game/SeaScene'

export function Dashboard() {
  const { user, refreshUser } = useAuth()
  const qc = useQueryClient()

  const missions = useMyMissions()

  // Subscribe to Mercure missions topic for live updates
  const handleMercure = useCallback(
    (data: MercureEventData) => {
      if (data.event === 'mission_completed') {
        qc.invalidateQueries({ queryKey: ['me', 'missions'] })
        qc.invalidateQueries({ queryKey: ['me', 'objectives'] })
        qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
        toast.success('⚓ Mission accomplie !', { duration: 5000 })
      }
    },
    [qc],
  )

  useMercureSubscription(
    user ? [`/users/${user.id}/missions`] : [],
    handleMercure,
    !!user,
  )

  // Robust fallback (Mercure-independent): when the missions data shows a NEW
  // completion — via the realtime event OR the polling fallback — the reward may
  // have moved quest progress and prestige/reputation. Refresh both, so the quest
  // unblocks and the prize lands without a manual refresh.
  const completedCount = missions.data?.missions.filter((m) => m.status === 'completed').length ?? 0
  const prevCompletedCount = useRef<number | null>(null)
  useEffect(() => {
    if (prevCompletedCount.current !== null && completedCount > prevCompletedCount.current) {
      qc.invalidateQueries({ queryKey: ['me', 'objectives'] })
      qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
      void refreshUser()
    }
    prevCompletedCount.current = completedCount
  }, [completedCount, qc, refreshUser])

  if (!user) return null

  const inProgress = missions.data?.missions.filter((m) => m.status === 'in_progress') ?? []
  const completed = (missions.data?.missions.filter((m) => m.status === 'completed') ?? [])
    .sort((a, b) => new Date(b.completedAt ?? b.resolvesAt).getTime() - new Date(a.completedAt ?? a.resolvesAt).getTime())
  // Onboarding: until the (one-shot) reconnaissance patrol exists, push it front
  // and centre and hold back the "go raid/trade" nudge (those are gated).
  // Authoritative server flag — NOT `missions.some(type==='patrol')`: the /me list
  // is capped at 20, so once the patrol ages out, that derived check would flip
  // back to false and the onboarding CTA would wrongly reappear (regression fixed).
  const patrolDone = missions.data?.patrolDone ?? false

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 pb-[300px] space-y-7">
      <div className="text-center py-4">
        <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-3">Tableau de bord</p>
        <h1 className="font-display text-4xl sm:text-5xl text-sand-300">
          Bienvenue capitaine <span className="text-sand-100">{user.pseudo}</span>
        </h1>
        <p className="text-ocean-100/80 mt-2">
          {patrolDone
            ? "Ton aventure dans l'archipel se poursuit. Le vent est favorable."
            : "Ton aventure commence. Lève l'ancre, capitaine."}
        </p>
      </div>

      {/* FTUE: hold back the reputation dial + the objectives wall until the
          reconnaissance patrol is done, so a brand-new captain sees ONE heroic
          action. Both reveal naturally when `patrolDone` flips on completion. */}
      {patrolDone && user.reputation && <ReputationCard reputation={user.reputation} />}

      {!patrolDone && <FirstPatrolCard />}

      {/* Carte au trésor — le parchemin du cycle courant. Rendue dès que la donnée
          arrive (la carte se présente dès le jour 1, comme la notif d'intro). */}
      <StreakCard />

      {patrolDone && inProgress.length === 0 && (
        <section className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-6 text-center">
          <p className="text-ocean-200">
            Aucune mission en cours. Cap sur la{' '}
            <Link to="/map" className="text-sand-300 underline underline-offset-4 hover:text-sand-100">carte du monde</Link>
            {' '}pour commercer, explorer ou raider.
          </p>
        </section>
      )}

      <InProgressPanel />

      {patrolDone && <ObjectivesPanel />}

      {completed.length > 0 && (
        <section>
          <h2 className="font-display text-xl text-sand-100 mb-3">Dernières missions terminées</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {completed.slice(0, 4).map((m) => <MissionCard key={m.id} mission={m} />)}
          </div>
        </section>
      )}

      {/* Integrated maritime backdrop — a fleet at sail, always present at the foot of the page. */}
      <SeaScene scene="map" />
    </div>
  )
}
